import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';

import { getRandomPercent } from '../../utils';

import { ChangingTip } from '../custom';
import { Spinner, ProgressBar } from '../general';

interface Props {
  heading: string;
  expectedMin: number;
  tipsList: string[];
  progressName?: string;
  className?: string;
}

const EstimatedProgress: React.FC<Props> = (props) => {
  const { heading, tipsList, expectedMin, progressName, className } = props;

  const [progress, setProgress] = useState(0);
  const [remainingMin, setRemainingMin] = useState(expectedMin);
  const [endTime, setEndTime] = useState(Date.now() + expectedMin * 60 * 1000);

  const startTime = useMemo(() => Date.now(), []);
  const total = useMemo(() => endTime - startTime, [startTime, endTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - startTime;
      const currentProgress = Math.round(Math.min((elapsed / total) * 100, 99));
      const timeLeft = endTime - now;

      setProgress(currentProgress);

      const remaining = Math.max(timeLeft / 60 / 1000, 0);
      setRemainingMin(remaining);

      // check if we should extend (when less than 10-30% time left)
      const threshold = total * getRandomPercent(10, 30);

      // 20% chance to extend when under threshold
      const chance = Math.random() < 0.2;

      if ((timeLeft < threshold && chance) || progress >= 95)
        // random extension: 5-25% of original expected time
        setEndTime((prev) => prev + getRandomPercent(5, 25) * 60 * 1000);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, endTime, total, progress]);

  return (
    <div
      className={classNames(
        { 'w-full': !className?.includes('w-') },
        'flex flex-col gap-4 items-center text-center',
        className,
      )}
    >
      <Spinner size="3xl" color="primary" />

      <h4>{heading}</h4>

      <div>This might take a bit. Feel free to check back later!</div>

      <ProgressBar
        progress={progress}
        fileName={progressName}
        estTime={Math.ceil(remainingMin)}
        isThin
        isBoxed
      />

      <ChangingTip tipsList={tipsList} />

      <hr className="my-4" />

      <div className="text-sm italic">
        Contact us at{' '}
        <a href="mailto:hello@crowdclip.com">hello@crowdclip.com</a> for further
        assistance.
      </div>
    </div>
  );
};

export default EstimatedProgress;
